import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Plus, Trash2, RefreshCw, ChevronDown, Archive,
  AlertTriangle, Info, CheckCircle2, Edit2, X, Check,
  Tag, StickyNote, Clock, ListChecks, Palette, RefreshCcw, Upload,
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { getEnvironment } from '../../utils/runtimeEnvironment';

/* ================================================================
   Constants
================================================================ */
const AREAS         = ['WEBAPP', 'MOBILE', 'API', 'LEGAL', 'MARKETING', 'OPS', 'OTHER'];
const STATUSES      = ['idea', 'backlog', 'in_progress', 'done', 'cancelled'];
const MAIN_STATUSES = ['idea', 'backlog', 'in_progress'];
const PRIORITIES    = ['low', 'medium', 'high'];

const PRESET_TAGS = ['bug', 'feature', 'refactor', 'urgent', 'design', 'review', 'test', 'deploy', 'docs', 'security'];
const TESTING_STATUSES = ['todo', 'in_progress', 'blocked', 'done'];

const TICKET_COLORS = [
  { value: null,     dotClass: 'bg-gray-300 dark:bg-white/25' },
  { value: 'purple', dotClass: 'bg-purple-500' },
  { value: 'red',    dotClass: 'bg-red-500' },
  { value: 'orange', dotClass: 'bg-orange-500' },
  { value: 'yellow', dotClass: 'bg-yellow-400' },
  { value: 'green',  dotClass: 'bg-emerald-500' },
  { value: 'blue',   dotClass: 'bg-blue-500' },
  { value: 'pink',   dotClass: 'bg-pink-500' },
  { value: 'teal',   dotClass: 'bg-teal-500' },
  { value: 'gray',   dotClass: 'bg-gray-500' },
];

/* ================================================================
   Utility functions
================================================================ */
const defaultFilters = () => ({
  area: 'all',
  status: 'all',
  assignee_user_id: '',
});

const emptyForm = () => ({
  area: 'WEBAPP',
  title: '',
  description: '',
  target_date: '',
  priority: 'medium',
  status: 'backlog',
  assignee_user_id: '',
  testing_status: 'todo',
  in_sprint: false,
  sprint_name: '',
});

function ticketToDetailForm(ticket) {
  return {
    title:           ticket.title          || '',
    description:     ticket.description    || '',
    priority:        ticket.priority       || 'medium',
    status:          ticket.status         || 'backlog',
    target_date:     ticket.target_date    || '',
    assignee_user_id: ticket.assignee_user_id != null ? String(ticket.assignee_user_id) : '',
    estimated_hours: ticket.estimated_hours != null ? String(ticket.estimated_hours) : '',
    tags:            Array.isArray(ticket.tags) ? ticket.tags : [],
    internal_notes:  ticket.internal_notes  || '',
    color:           ticket.color           || null,
    testing_status:  ticket.testing_status || 'todo',
    in_sprint:       Number(ticket.in_sprint) === 1 || !!ticket.sprint_name,
    sprint_name:     ticket.sprint_name || '',
  };
}

function colorBorderClass(color) {
  const map = {
    purple: 'border-l-[3px] border-l-purple-500',
    red:    'border-l-[3px] border-l-red-500',
    orange: 'border-l-[3px] border-l-orange-500',
    yellow: 'border-l-[3px] border-l-yellow-400',
    green:  'border-l-[3px] border-l-emerald-500',
    blue:   'border-l-[3px] border-l-blue-500',
    pink:   'border-l-[3px] border-l-pink-500',
    teal:   'border-l-[3px] border-l-teal-500',
    gray:   'border-l-[3px] border-l-gray-500',
  };
  return color && map[color] ? map[color] : '';
}

function colorDotClass(color) {
  const map = {
    purple: 'bg-purple-500',
    red:    'bg-red-500',
    orange: 'bg-orange-500',
    yellow: 'bg-yellow-400',
    green:  'bg-emerald-500',
    blue:   'bg-blue-500',
    pink:   'bg-pink-500',
    teal:   'bg-teal-500',
    gray:   'bg-gray-500',
  };
  return color && map[color] ? map[color] : 'bg-gray-300 dark:bg-white/25';
}

function userLabel(u) {
  const base = u.username || u.name || `#${u.id}`;
  const name = u.name && u.name !== u.username ? ` — ${u.name}` : '';
  const role = u.role_name ? ` (${u.role_name})` : '';
  return `${base}${name}${role}`;
}

function isActiveAdminUser(u) {
  return u && u.active == 1 && String(u.role_name || '').toLowerCase() === 'admin';
}

function buildAppliedFiltersSummary(f, t, users) {
  const parts = [];
  if (f.area && f.area !== 'all') parts.push(f.area);
  if (f.status && f.status !== 'all') parts.push(t(`admin.planningBoard.statuses.${f.status}`));
  if (f.assignee_user_id === 'unassigned') {
    parts.push(t('admin.planningBoard.filterUnassigned'));
  } else if (f.assignee_user_id) {
    const id = parseInt(String(f.assignee_user_id), 10);
    if (Number.isFinite(id) && id > 0) {
      const u = (users || []).find((x) => Number(x.id) === id);
      parts.push(u ? userLabel(u) : `#${id}`);
    }
  }
  if (parts.length === 0) return t('admin.planningBoard.filtersSummaryDefault');
  return parts.join(' · ');
}

function formatDate(isoStr) {
  if (!isoStr) return '—';
  try {
    return new Date(isoStr).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return isoStr; }
}

/* ================================================================
   PlanningTicketsDenseTable (vista backlog / “tabella”)
================================================================ */
function statusSortKey(status) {
  const i = STATUSES.indexOf(status);
  return i === -1 ? 99 : i;
}

function PlanningTicketsDenseTable({
  tickets,
  t,
  getAssigneeSelectOptions,
  onStatusChange,
  onAssigneeChange,
  onPriorityChange,
  onTestingStatusChange,
  onDelete,
  onOpenDetail,
  isTestEnv,
  getProdStatus,
}) {
  const sorted = useMemo(() => {
    const list = Array.isArray(tickets) ? [...tickets] : [];
    list.sort((a, b) => {
      const o = statusSortKey(a.status) - statusSortKey(b.status);
      if (o !== 0) return o;
      return String(b.updated_at || '').localeCompare(String(a.updated_at || ''));
    });
    return list;
  }, [tickets]);

  const selectCls =
    'w-full min-w-0 max-w-full rounded border border-gray-200 bg-white px-1 py-0.5 text-[11px] text-gray-900 dark:border-white/10 dark:bg-gray-900 dark:text-white';

  if (sorted.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-4 py-12 text-center text-sm text-gray-500 dark:border-white/10 dark:bg-gray-900/90 dark:text-white/60">
        {t('admin.planningBoard.tableEmpty')}
      </div>
    );
  }

  return (
    <div className="max-h-[min(72vh,calc(100vh-14rem))] overflow-auto rounded-xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-gray-900/90">
      <table className="w-full min-w-[1180px] border-collapse text-left text-[11px]">
        <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:border-white/10 dark:bg-gray-900 dark:text-white/55">
          <tr>
            <th className="whitespace-nowrap px-2 py-2">{t('admin.planningBoard.tableColumns.code')}</th>
            <th className="min-w-[220px] px-2 py-2">{t('admin.planningBoard.fields.title')}</th>
            <th className="whitespace-nowrap px-2 py-2">{t('admin.planningBoard.fields.area')}</th>
            <th className="whitespace-nowrap px-2 py-2">{t('admin.planningBoard.fields.initialStatus')}</th>
            <th className="whitespace-nowrap px-2 py-2">{t('admin.planningBoard.fields.priority')}</th>
            <th className="min-w-[150px] px-2 py-2">{t('admin.planningBoard.fields.assignee')}</th>
            <th className="whitespace-nowrap px-2 py-2">{t('admin.planningBoard.fields.testingStatus')}</th>
            <th className="min-w-[120px] px-2 py-2">{t('admin.planningBoard.tableColumns.sprint')}</th>
            <th className="whitespace-nowrap px-2 py-2">{t('admin.planningBoard.fields.targetDate')}</th>
            <th className="whitespace-nowrap px-2 py-2 text-center">{t('admin.planningBoard.tableColumns.checklist')}</th>
            {isTestEnv ? (
              <th className="whitespace-nowrap px-2 py-2 text-center">{t('admin.planningBoard.tableColumns.prod')}</th>
            ) : null}
            <th className="whitespace-nowrap px-2 py-2 text-right">{t('admin.planningBoard.tableColumns.actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 text-gray-800 dark:divide-white/10 dark:text-white">
          {sorted.map((ticket) => {
            const assigneeOpts = getAssigneeSelectOptions(ticket.assignee_user_id);
            const prod = isTestEnv ? getProdStatus(ticket.ticket_code) : null;
            const prodColor =
              prod === 'missing' ? 'bg-red-500'
              : prod === 'outdated' ? 'bg-amber-400'
              : prod === 'synced' ? 'bg-emerald-500'
              : 'bg-gray-300 dark:bg-white/25';
            const prodTitle =
              prod === 'missing' ? t('admin.planningBoard.prodStatus.missing')
              : prod === 'outdated' ? t('admin.planningBoard.prodStatus.outdated')
              : prod === 'synced' ? t('admin.planningBoard.prodStatus.synced')
              : '';
            const sprintCell =
              Number(ticket.in_sprint) === 1 || (ticket.sprint_name && String(ticket.sprint_name).trim())
                ? ((ticket.sprint_name || '').trim() || t('admin.planningBoard.inSprint'))
                : '—';
            const isBlocked = ticket.testing_status === 'blocked';
            const cl = Number(ticket.checklist_total) > 0
              ? `${ticket.checklist_done}/${ticket.checklist_total}`
              : '—';

            return (
              <tr
                key={ticket.id}
                className={`hover:bg-gray-50/90 dark:hover:bg-white/5 ${isBlocked ? 'bg-amber-500/10 dark:bg-amber-500/10' : ''}`}
              >
                <td className={`whitespace-nowrap px-2 py-1.5 font-mono text-[11px] font-semibold text-indigo-600 dark:text-indigo-300 ${colorBorderClass(ticket.color)}`}>
                  {ticket.ticket_code}
                </td>
                <td className="max-w-[320px] px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => onOpenDetail(ticket)}
                    className="block w-full truncate text-left font-medium text-gray-900 underline-offset-2 hover:underline dark:text-white"
                    title={ticket.title}
                  >
                    {ticket.title}
                  </button>
                </td>
                <td className="whitespace-nowrap px-2 py-1.5">{ticket.area}</td>
                <td className="w-[124px] max-w-[124px] px-2 py-1.5">
                  <select
                    value={ticket.status}
                    onChange={(e) => onStatusChange(ticket.id, e.target.value)}
                    className={selectCls}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{t(`admin.planningBoard.statuses.${s}`)}</option>
                    ))}
                  </select>
                </td>
                <td className="w-[104px] max-w-[104px] px-2 py-1.5">
                  <select
                    value={ticket.priority}
                    onChange={(e) => onPriorityChange(ticket.id, e.target.value)}
                    className={selectCls}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>{t(`admin.planningBoard.priorities.${p}`)}</option>
                    ))}
                  </select>
                </td>
                <td className="min-w-[160px] px-2 py-1.5">
                  <select
                    value={ticket.assignee_user_id != null ? String(ticket.assignee_user_id) : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      onAssigneeChange(ticket.id, v === '' ? null : parseInt(v, 10));
                    }}
                    className={selectCls}
                  >
                    <option value="">{t('admin.planningBoard.assigneeNone')}</option>
                    {assigneeOpts.map((u) => (
                      <option key={u.id} value={String(u.id)}>{userLabel(u)}</option>
                    ))}
                  </select>
                </td>
                <td className="w-[132px] max-w-[132px] px-2 py-1.5">
                  <select
                    value={ticket.testing_status || 'todo'}
                    onChange={(e) => onTestingStatusChange(ticket.id, e.target.value)}
                    className={selectCls}
                  >
                    {TESTING_STATUSES.map((ts) => (
                      <option key={ts} value={ts}>{t(`admin.planningBoard.testingStatuses.${ts}`)}</option>
                    ))}
                  </select>
                </td>
                <td className="max-w-[200px] truncate px-2 py-1.5 text-gray-600 dark:text-white/70" title={sprintCell === '—' ? '' : sprintCell}>
                  {sprintCell}
                </td>
                <td className="whitespace-nowrap px-2 py-1.5 text-gray-600 dark:text-white/65">
                  {ticket.target_date || '—'}
                </td>
                <td className="px-2 py-1.5 text-center tabular-nums text-gray-600 dark:text-white/65">{cl}</td>
                {isTestEnv ? (
                  <td className="px-2 py-1.5 text-center">
                    <span className={`inline-block h-2 w-2 rounded-full ${prodColor}`} title={prodTitle} />
                  </td>
                ) : null}
                <td className="whitespace-nowrap px-2 py-1.5 text-right">
                  <div className="inline-flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => onOpenDetail(ticket)}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-indigo-600 dark:hover:bg-white/10 dark:hover:text-[#bba8e3]"
                      title={t('admin.planningBoard.detail.openDetails')}
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(ticket.id)}
                      className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                      title={t('admin.planningBoard.delete')}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ================================================================
   TicketDetailModal
================================================================ */
function TicketDetailModal({
  open,
  onClose,
  ticket,
  api,
  t,
  isTestEnv,
  getAssigneeSelectOptions,
  adminAssigneeOptions,
  priorityClass,
  pendingProdCodes,
  outdatedProdCodes,
  onSaved,
  onPushToProd,
  pushBusy,
  onUpdateInProd,
  updateBusy,
}) {
  const [form, setForm]               = useState(null);
  const [checklist, setChecklist]     = useState([]);
  const [clLoading, setClLoading]     = useState(false);
  const [newItemText, setNewItemText] = useState('');
  const [addingItem, setAddingItem]   = useState(false);
  const [saving, setSaving]           = useState(false);
  const [tagInput, setTagInput]       = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && ticket) {
      setForm(ticketToDetailForm(ticket));
      setNewItemText('');
      setTagInput('');
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }
    if (!open) {
      setChecklist([]);
      setForm(null);
    }
  }, [open, ticket?.id]);

  useEffect(() => {
    if (open && ticket?.id && api) {
      loadChecklist();
    }
  }, [open, ticket?.id]);

  const loadChecklist = async () => {
    if (!api || !ticket) return;
    setClLoading(true);
    try {
      const res = await api.get('/admin_planning_tickets.php', { params: { action: 'get', id: ticket.id } });
      setChecklist(Array.isArray(res.data?.checklist) ? res.data.checklist : []);
    } catch {
      setChecklist([]);
    } finally {
      setClLoading(false);
    }
  };

  const handleSave = async () => {
    if (!api || !form || !ticket) return;
    const title = form.title.trim();
    if (!title) { toast.error(t('admin.planningBoard.errors.titleRequired')); return; }
    setSaving(true);
    try {
      await api.post('/admin_planning_tickets.php', {
        action:           'update',
        id:               ticket.id,
        title,
        description:      form.description.trim() || null,
        priority:         form.priority,
        status:           form.status,
        target_date:      form.target_date || null,
        assignee_user_id: form.assignee_user_id !== '' ? parseInt(form.assignee_user_id, 10) : null,
        estimated_hours:  form.estimated_hours !== '' ? parseFloat(form.estimated_hours) : null,
        tags:             form.tags,
        internal_notes:   form.internal_notes.trim() || null,
        color:            form.color,
        testing_status:   form.testing_status,
        in_sprint:        form.in_sprint ? 1 : 0,
        sprint_name:      form.in_sprint ? (form.sprint_name.trim() || null) : null,
      });
      toast.success(t('admin.planningBoard.toast.updated'));
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.planningBoard.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const handleChecklistAdd = async () => {
    const text = newItemText.trim();
    if (!text || !api || !ticket) return;
    setAddingItem(true);
    try {
      const res = await api.post('/admin_planning_tickets.php', {
        action: 'checklist_add', ticket_id: ticket.id, text,
      });
      setChecklist((prev) => [...prev, res.data.item]);
      setNewItemText('');
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.planningBoard.errors.save'));
    } finally {
      setAddingItem(false);
    }
  };

  const handleChecklistToggle = async (itemId, isDone) => {
    if (!api) return;
    const newVal = isDone ? 0 : 1;
    setChecklist((prev) => prev.map((c) => c.id === itemId ? { ...c, is_done: newVal } : c));
    try {
      await api.post('/admin_planning_tickets.php', { action: 'checklist_toggle', id: itemId, is_done: newVal });
    } catch (err) {
      setChecklist((prev) => prev.map((c) => c.id === itemId ? { ...c, is_done: isDone ? 1 : 0 } : c));
      toast.error(err.response?.data?.error || t('admin.planningBoard.errors.save'));
    }
  };

  const handleChecklistDelete = async (itemId) => {
    if (!api) return;
    const prev = checklist;
    setChecklist((c) => c.filter((x) => x.id !== itemId));
    try {
      await api.post('/admin_planning_tickets.php', { action: 'checklist_delete', id: itemId });
    } catch (err) {
      setChecklist(prev);
      toast.error(err.response?.data?.error || t('admin.planningBoard.errors.save'));
    }
  };

  const handleTagAdd = (tag) => {
    if (!form) return;
    const clean = tag.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '');
    if (!clean || form.tags.includes(clean) || form.tags.length >= 10) return;
    setForm((f) => ({ ...f, tags: [...f.tags, clean] }));
    setTagInput('');
  };

  const handleTagRemove = (tag) => {
    setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== tag) }));
  };

  if (!ticket || !form) return null;

  const assigneeOpts = getAssigneeSelectOptions(
    form.assignee_user_id !== '' ? parseInt(form.assignee_user_id, 10) : null
  );

  const clTotal = checklist.length;
  const clDone  = checklist.filter((c) => c.is_done).length;
  const clPct   = clTotal > 0 ? Math.round((clDone / clTotal) * 100) : 0;

  const isMissing  = pendingProdCodes.has(ticket.ticket_code);
  const isOutdated = !isMissing && outdatedProdCodes.has(ticket.ticket_code);
  const isSynced   = !isMissing && !isOutdated;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[min(92vh,860px)] max-w-2xl flex-col gap-0 overflow-hidden p-0 dark:border-white/10 dark:bg-gray-900 sm:rounded-xl">

        {/* Header */}
        <DialogHeader className="shrink-0 border-b border-gray-100 px-6 py-4 dark:border-white/10">
          <div className="flex items-center gap-3">
            {ticket.color && (
              <span className={`h-3 w-3 shrink-0 rounded-full ${colorDotClass(ticket.color)}`} />
            )}
            <div className="min-w-0 flex-1">
              <DialogTitle className="flex flex-wrap items-center gap-2 text-base text-gray-900 dark:text-white">
                <span className="font-mono text-sm font-semibold text-indigo-600 dark:text-indigo-300">
                  {ticket.ticket_code}
                </span>
                <span className="truncate">{form.title || ticket.title}</span>
              </DialogTitle>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-white/50">
                {ticket.area} · {t('admin.planningBoard.detail.createdBy')}: {ticket.created_by_username || '—'} · {t('admin.planningBoard.detail.updatedAt')}: {formatDate(ticket.updated_at)}
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable body */}
        <div ref={scrollRef} className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">

          {/* PROD Status banner (solo env TEST) */}
          {isTestEnv && (
            <div className={`rounded-xl border px-4 py-3 ${
              isMissing
                ? 'border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/20'
                : isOutdated
                  ? 'border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20'
                  : 'border-emerald-200 bg-emerald-50/80 dark:border-emerald-900/40 dark:bg-emerald-950/20'
            }`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {isMissing  && <AlertTriangle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />}
                  {isOutdated && <RefreshCcw className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />}
                  {isSynced   && <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />}
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {isMissing  && t('admin.planningBoard.prodStatus.missingLabel')}
                    {isOutdated && t('admin.planningBoard.prodStatus.outdatedLabel')}
                    {isSynced   && t('admin.planningBoard.prodStatus.syncedLabel')}
                  </span>
                </div>
                {isMissing && (
                  <button
                    type="button"
                    onClick={() => onPushToProd(ticket.id)}
                    disabled={pushBusy}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    <Upload className="h-3.5 w-3.5" />
                    {pushBusy ? t('admin.planningBoard.pushToProdWorking') : t('admin.planningBoard.pushToProd')}
                  </button>
                )}
                {isOutdated && (
                  <button
                    type="button"
                    onClick={() => onUpdateInProd(ticket.id)}
                    disabled={updateBusy}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                  >
                    <RefreshCcw className="h-3.5 w-3.5" />
                    {updateBusy ? t('admin.planningBoard.updateProdWorking') : t('admin.planningBoard.updateProd')}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Titolo */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-white/70 mb-1">
              {t('admin.planningBoard.fields.title')}
            </label>
            <input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              maxLength={255}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 dark:border-white/10 dark:bg-gray-900 dark:text-white"
            />
          </div>

          {/* Griglia: priorità / stato / data / assegnatario / stima */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.area')}</span>
              <span className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-mono font-semibold text-indigo-600 dark:border-white/10 dark:bg-gray-900 dark:text-indigo-300">
                {ticket.area}
              </span>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.priority')}</span>
              <select
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 dark:border-white/10 dark:bg-gray-900 dark:text-white"
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>{t(`admin.planningBoard.priorities.${p}`)}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.initialStatus')}</span>
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 dark:border-white/10 dark:bg-gray-900 dark:text-white"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{t(`admin.planningBoard.statuses.${s}`)}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.targetDate')}</span>
              <input
                type="date"
                value={form.target_date}
                onChange={(e) => setForm((f) => ({ ...f, target_date: e.target.value }))}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 dark:border-white/10 dark:bg-gray-900 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.testingStatus')}</span>
              <select
                value={form.testing_status}
                onChange={(e) => setForm((f) => ({ ...f, testing_status: e.target.value }))}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 dark:border-white/10 dark:bg-gray-900 dark:text-white"
              >
                {TESTING_STATUSES.map((ts) => (
                  <option key={ts} value={ts}>{t(`admin.planningBoard.testingStatuses.${ts}`)}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium text-gray-600 dark:text-white/70">
                <Clock className="inline h-3 w-3 mr-0.5" />{t('admin.planningBoard.detail.estimatedHours')}
              </span>
              <input
                type="number"
                min="0"
                max="9999"
                step="0.5"
                value={form.estimated_hours}
                onChange={(e) => setForm((f) => ({ ...f, estimated_hours: e.target.value }))}
                placeholder="—"
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 dark:border-white/10 dark:bg-gray-900 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs sm:col-span-1">
              <span className="font-medium text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.assignee')}</span>
              <select
                value={form.assignee_user_id}
                onChange={(e) => setForm((f) => ({ ...f, assignee_user_id: e.target.value }))}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 dark:border-white/10 dark:bg-gray-900 dark:text-white"
              >
                <option value="">{t('admin.planningBoard.assigneeNone')}</option>
                {assigneeOpts.map((u) => (
                  <option key={u.id} value={String(u.id)}>{userLabel(u)}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs dark:border-white/10 dark:bg-gray-900/80">
              <input
                type="checkbox"
                checked={form.in_sprint}
                onChange={(e) => setForm((f) => ({ ...f, in_sprint: e.target.checked, sprint_name: e.target.checked ? f.sprint_name : '' }))}
                className="rounded border-gray-300"
              />
              <span className="font-medium text-gray-700 dark:text-white/80">{t('admin.planningBoard.fields.inSprint')}</span>
            </label>
            {form.in_sprint ? (
              <label className="flex flex-col gap-1 text-xs sm:col-span-2">
                <span className="font-medium text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.sprintName')}</span>
                <input
                  value={form.sprint_name}
                  onChange={(e) => setForm((f) => ({ ...f, sprint_name: e.target.value }))}
                  maxLength={120}
                  placeholder={t('admin.planningBoard.placeholders.sprintName')}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 dark:border-white/10 dark:bg-gray-900 dark:text-white"
                />
              </label>
            ) : null}
          </div>

          {/* Descrizione */}
          <label className="block text-xs">
            <span className="mb-1 block font-medium text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.description')}</span>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 dark:border-white/10 dark:bg-gray-900 dark:text-white"
              placeholder={t('admin.planningBoard.placeholders.description')}
            />
          </label>

          {/* ── Colore ── */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <Palette className="h-4 w-4 shrink-0 text-gray-500 dark:text-white/55" />
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-white/60">
                {t('admin.planningBoard.detail.color')}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {TICKET_COLORS.map(({ value, dotClass }) => (
                <button
                  key={value ?? 'none'}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, color: value }))}
                  title={value ?? t('admin.planningBoard.detail.colorNone')}
                  className={`h-7 w-7 rounded-full ${dotClass} transition-transform hover:scale-110 ${
                    form.color === value
                      ? 'scale-110 ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-gray-950'
                      : ''
                  }`}
                />
              ))}
            </div>
          </div>

          {/* ── Tags ── */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <Tag className="h-4 w-4 shrink-0 text-gray-500 dark:text-white/55" />
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-white/60">
                {t('admin.planningBoard.detail.tags')}
              </span>
            </div>
            {/* Existing tags */}
            <div className="mb-2 flex flex-wrap gap-1.5">
              {form.tags.length === 0 && (
                <span className="text-xs text-gray-400 dark:text-white/35">{t('admin.planningBoard.detail.noTags')}</span>
              )}
              {form.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-full bg-indigo-600/15 px-2.5 py-0.5 text-xs font-medium text-indigo-600 dark:text-indigo-300"
                >
                  {tag}
                  <button type="button" onClick={() => handleTagRemove(tag)} className="hover:text-red-500">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            {/* Add tag */}
            <div className="mb-2 flex gap-2">
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleTagAdd(tagInput); } }}
                placeholder={t('admin.planningBoard.detail.tagPlaceholder')}
                maxLength={32}
                className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-900 dark:border-white/10 dark:bg-gray-900 dark:text-white"
              />
              <button
                type="button"
                onClick={() => handleTagAdd(tagInput)}
                disabled={!tagInput.trim() || form.tags.length >= 10}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40 dark:border-white/10 dark:text-white dark:hover:bg-white/10"
              >
                {t('admin.planningBoard.detail.tagAdd')}
              </button>
            </div>
            {/* Preset suggestions */}
            <div className="flex flex-wrap gap-1">
              {PRESET_TAGS.filter((pt) => !form.tags.includes(pt)).map((pt) => (
                <button
                  key={pt}
                  type="button"
                  onClick={() => handleTagAdd(pt)}
                  disabled={form.tags.length >= 10}
                  className="rounded-full border border-gray-200 px-2 py-0.5 text-[11px] text-gray-500 hover:bg-gray-100 disabled:opacity-30 dark:border-white/10 dark:text-white/50 dark:hover:bg-white/10"
                >
                  +{pt}
                </button>
              ))}
            </div>
          </div>

          {/* ── Note interne ── */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <StickyNote className="h-4 w-4 shrink-0 text-gray-500 dark:text-white/55" />
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-white/60">
                {t('admin.planningBoard.detail.notes')}
              </span>
            </div>
            <textarea
              value={form.internal_notes}
              onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))}
              rows={3}
              placeholder={t('admin.planningBoard.detail.notesPlaceholder')}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 dark:border-white/10 dark:bg-gray-900 dark:text-white"
            />
          </div>

          {/* ── Checklist ── */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <ListChecks className="h-4 w-4 shrink-0 text-gray-500 dark:text-white/55" />
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-white/60">
                {t('admin.planningBoard.detail.checklist')}
              </span>
              {clTotal > 0 && (
                <span className="ml-auto text-xs text-gray-500 dark:text-white/50">
                  {clDone}/{clTotal}
                </span>
              )}
            </div>

            {/* Progress bar */}
            {clTotal > 0 && (
              <div className="mb-3 h-1.5 rounded-full bg-gray-200 dark:bg-white/10">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                  style={{ width: `${clPct}%` }}
                />
              </div>
            )}

            {clLoading ? (
              <div className="flex justify-center py-4">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
              </div>
            ) : (
              <>
                {checklist.length === 0 && (
                  <p className="mb-3 text-xs text-gray-400 dark:text-white/35">
                    {t('admin.planningBoard.detail.noChecklist')}
                  </p>
                )}
                <div className="mb-3 space-y-1">
                  {checklist.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-white/5"
                    >
                      <button
                        type="button"
                        onClick={() => handleChecklistToggle(item.id, item.is_done)}
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                          item.is_done
                            ? 'border-emerald-500 bg-emerald-500 text-white'
                            : 'border-gray-300 hover:border-emerald-400 dark:border-white/30'
                        }`}
                      >
                        {item.is_done ? <Check className="h-2.5 w-2.5" /> : null}
                      </button>
                      <span
                        className={`flex-1 text-sm ${
                          item.is_done
                            ? 'text-gray-400 line-through dark:text-white/35'
                            : 'text-gray-800 dark:text-white'
                        }`}
                      >
                        {item.text}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleChecklistDelete(item.id)}
                        className="shrink-0 text-gray-300 hover:text-red-500 dark:text-white/25 dark:hover:text-red-400"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new item */}
                <div className="flex gap-2">
                  <input
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleChecklistAdd(); } }}
                    placeholder={t('admin.planningBoard.detail.checklistPlaceholder')}
                    maxLength={512}
                    className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 dark:border-white/10 dark:bg-gray-900 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={handleChecklistAdd}
                    disabled={!newItemText.trim() || addingItem}
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 text-white hover:bg-[#5529a8] disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Metadata */}
          <div className="border-t border-gray-100 pt-3 dark:border-white/10">
            <p className="text-xs text-gray-400 dark:text-white/30">
              {t('admin.planningBoard.detail.createdAt')}: {formatDate(ticket.created_at)}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4 dark:border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-white dark:hover:bg-white/10"
          >
            {t('admin.planningBoard.detail.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-[#5529a8] disabled:opacity-50"
          >
            {saving && <RefreshCw className="h-4 w-4 animate-spin" />}
            {t('admin.planningBoard.detail.save')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================
   AdminPlanningBoard (main)
================================================================ */
const AdminPlanningBoard = () => {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();
  const { t } = useTranslation();

  const [tickets,          setTickets]          = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [saving,           setSaving]           = useState(false);
  const [form,             setForm]             = useState(emptyForm);
  const [filterForm,       setFilterForm]       = useState(defaultFilters);
  const [appliedFilters,   setAppliedFilters]   = useState(defaultFilters);
  const [users,            setUsers]            = useState([]);
  const [filtersExpanded,  setFiltersExpanded]  = useState(false);
  const [newTicketExpanded,setNewTicketExpanded]= useState(false);
  const [archiveDialogOpen,setArchiveDialogOpen]= useState(false);
  const [syncInfoDialogOpen, setSyncInfoDialogOpen] = useState(false);

  // Sync prod
  const [pendingProdCodes, setPendingProdCodes] = useState(() => new Set());
  const [outdatedProdCodes,setOutdatedProdCodes]= useState(() => new Set());
  const [pushingTicketId,  setPushingTicketId]  = useState(null);
  const [updatingTicketId, setUpdatingTicketId] = useState(null);
  const [syncMeta, setSyncMeta] = useState({
    loaded: false, checking: false, available: null,
    serverMessage: '', error: '', pendingCount: 0, outdatedCount: 0, lastChecked: null,
  });

  // Detail modal
  const [detailModalTicket, setDetailModalTicket] = useState(null);

  const isTestEnv = getEnvironment() === 'test';

  const adminAssigneeOptions = useMemo(
    () => [...users].filter(isActiveAdminUser)
      .sort((a, b) => (a.username || '').localeCompare(b.username || '', undefined, { sensitivity: 'base' })),
    [users]
  );

  const getAssigneeSelectOptions = useCallback(
    (assigneeUserId) => {
      const id = assigneeUserId != null && Number.isFinite(Number(assigneeUserId)) && Number(assigneeUserId) > 0
        ? Number(assigneeUserId) : null;
      if (id == null) return adminAssigneeOptions;
      if (adminAssigneeOptions.some((u) => u.id === id)) return adminAssigneeOptions;
      const current = users.find((u) => u.id === id);
      if (!current) return adminAssigneeOptions;
      return [current, ...adminAssigneeOptions];
    },
    [adminAssigneeOptions, users]
  );

  const newTicketAssigneeOptions = useMemo(() => {
    const raw = form.assignee_user_id;
    if (raw === '' || raw == null) return getAssigneeSelectOptions(null);
    const n = parseInt(String(raw), 10);
    return getAssigneeSelectOptions(Number.isFinite(n) && n > 0 ? n : null);
  }, [form.assignee_user_id, getAssigneeSelectOptions]);

  const loadUsers = useCallback(async () => {
    if (!api) return;
    try {
      const res = await api.get('/users.php');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch { setUsers([]); }
  }, [api]);

  const loadTickets = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const params = {
        action: 'list',
        status: appliedFilters.status,
        area: appliedFilters.area,
      };
      if (appliedFilters.assignee_user_id === 'unassigned') {
        params.assignee_user_id = 'unassigned';
      } else if (appliedFilters.assignee_user_id) {
        const aid = parseInt(String(appliedFilters.assignee_user_id), 10);
        if (Number.isFinite(aid) && aid > 0) params.assignee_user_id = String(aid);
      }
      const res = await api.get('/admin_planning_tickets.php', { params });
      setTickets(Array.isArray(res.data?.tickets) ? res.data.tickets : []);
    } catch (e) {
      toast.error(e.response?.data?.error || t('admin.planningBoard.errors.load'));
      setTickets([]);
    } finally { setLoading(false); }
  }, [api, appliedFilters, t]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadTickets(); }, [loadTickets]);

  const refreshProdSync = useCallback(async () => {
    if (!api || !isTestEnv) {
      setPendingProdCodes(new Set());
      setOutdatedProdCodes(new Set());
      setSyncMeta({ loaded: true, checking: false, available: null, serverMessage: '', error: '', pendingCount: 0, outdatedCount: 0, lastChecked: null });
      return;
    }
    setSyncMeta((m) => ({ ...m, checking: true, error: '' }));
    try {
      const res = await api.get('/admin_planning_tickets_sync.php', { params: { action: 'list_missing_for_prod' } });
      const missing  = Array.isArray(res.data?.missing_ticket_codes)  ? res.data.missing_ticket_codes  : [];
      const outdated = Array.isArray(res.data?.outdated_ticket_codes) ? res.data.outdated_ticket_codes : [];
      const available = res.data?.sync_available !== false;
      const serverMessage = typeof res.data?.message === 'string' ? res.data.message : '';
      setPendingProdCodes(new Set(missing));
      setOutdatedProdCodes(new Set(outdated));
      setSyncMeta({
        loaded: true, checking: false, available, serverMessage, error: '',
        pendingCount: missing.length, outdatedCount: outdated.length,
        lastChecked: new Date().toISOString(),
      });
    } catch (e) {
      const errMsg = e.response?.data?.error || t('admin.planningBoard.syncBannerError');
      setPendingProdCodes(new Set());
      setOutdatedProdCodes(new Set());
      setSyncMeta({ loaded: true, checking: false, available: false, serverMessage: '', error: errMsg, pendingCount: 0, outdatedCount: 0, lastChecked: null });
    }
  }, [api, isTestEnv, t]);

  useEffect(() => {
    if (!isTestEnv) {
      setPendingProdCodes(new Set());
      setOutdatedProdCodes(new Set());
      setSyncMeta({ loaded: false, checking: false, available: null, serverMessage: '', error: '', pendingCount: 0, outdatedCount: 0, lastChecked: null });
      return;
    }
    refreshProdSync();
  }, [isTestEnv, refreshProdSync, tickets]);

  const getProdStatus = useCallback((ticketCode) => {
    if (!isTestEnv) return null;
    if (pendingProdCodes.has(ticketCode))  return 'missing';
    if (outdatedProdCodes.has(ticketCode)) return 'outdated';
    return 'synced';
  }, [isTestEnv, pendingProdCodes, outdatedProdCodes]);

  const archivedTickets = useMemo(() =>
    tickets.filter((x) => x.status === 'done' || x.status === 'cancelled')
      .sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')))
  , [tickets]);

  /** Lista principale: niente completati/annullati (restano nel dialog archivio), salvo filtro stato esplicito. */
  const mainListTickets = useMemo(() => {
    const s = appliedFilters.status;
    if (s === 'done' || s === 'cancelled') {
      return tickets.filter((x) => x.status === s);
    }
    return tickets.filter((x) => x.status !== 'done' && x.status !== 'cancelled');
  }, [tickets, appliedFilters.status]);

  const boardStats = useMemo(() => {
    const active = tickets.filter((x) => MAIN_STATUSES.includes(x.status));
    return {
      totalActive: active.length,
      inSprint: active.filter((x) => Number(x.in_sprint) === 1 || !!x.sprint_name).length,
      testingDone: active.filter((x) => x.testing_status === 'done').length,
      testingBlocked: active.filter((x) => x.testing_status === 'blocked').length,
    };
  }, [tickets]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!api) return;
    const title = form.title.trim();
    if (!title) { toast.error(t('admin.planningBoard.errors.titleRequired')); return; }
    setSaving(true);
    try {
      const payload = {
        action: 'create', area: form.area, title,
        description: form.description.trim() || null,
        priority: form.priority, status: form.status,
        testing_status: form.testing_status,
        in_sprint: form.in_sprint ? 1 : 0,
      };
      if (form.in_sprint && form.sprint_name.trim()) payload.sprint_name = form.sprint_name.trim();
      if (form.target_date) payload.target_date = form.target_date;
      if (form.assignee_user_id) {
        const aid = parseInt(String(form.assignee_user_id), 10);
        if (aid > 0) payload.assignee_user_id = aid;
      }
      await api.post('/admin_planning_tickets.php', payload);
      toast.success(t('admin.planningBoard.toast.created'));
      setForm(emptyForm());
      await loadTickets();
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.planningBoard.errors.save'));
    } finally { setSaving(false); }
  };

  const patchTicket = async (id, body) => {
    if (!api) return;
    try {
      await api.post('/admin_planning_tickets.php', { action: 'update', id, ...body });
      await loadTickets();
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.planningBoard.errors.save'));
    }
  };

  const handleStatusChange   = (id, status)         => patchTicket(id, { status });
  const handleAssigneeChange = (id, assigneeUserId)  => patchTicket(id, { assignee_user_id: assigneeUserId });
  const handlePriorityChange = (id, priority)       => patchTicket(id, { priority });
  const handleTestingStatusChange = (id, testing_status) => patchTicket(id, { testing_status });

  const handleDelete = (id) => {
    if (!window.confirm(t('admin.planningBoard.confirmDelete'))) return;
    (async () => {
      if (!api) return;
      try {
        await api.post('/admin_planning_tickets.php', { action: 'delete', id });
        toast.success(t('admin.planningBoard.toast.deleted'));
        await loadTickets();
      } catch (err) {
        toast.error(err.response?.data?.error || t('admin.planningBoard.errors.delete'));
      }
    })();
  };

  const handlePushToProd = async (ticketId) => {
    if (!api) return;
    setPushingTicketId(ticketId);
    try {
      const res = await api.post('/admin_planning_tickets_sync.php', { action: 'push_ticket_to_prod', ticket_id: ticketId });
      if (res.data?.already_in_prod) {
        toast.success(t('admin.planningBoard.pushAlreadyProd'));
      } else {
        toast.success(t('admin.planningBoard.pushSuccess'));
      }
      await refreshProdSync();
    } catch (e) {
      toast.error(e.response?.data?.error || t('admin.planningBoard.pushError'));
    } finally { setPushingTicketId(null); }
  };

  const handleUpdateInProd = async (ticketId) => {
    if (!api) return;
    setUpdatingTicketId(ticketId);
    try {
      await api.post('/admin_planning_tickets_sync.php', { action: 'update_ticket_in_prod', ticket_id: ticketId });
      toast.success(t('admin.planningBoard.updateProdSuccess'));
      await refreshProdSync();
    } catch (e) {
      toast.error(e.response?.data?.error || t('admin.planningBoard.updateProdError'));
    } finally { setUpdatingTicketId(null); }
  };

  const priorityClass = (p) => {
    if (p === 'high')   return 'bg-red-500/15 text-red-700 dark:text-red-300';
    if (p === 'medium') return 'bg-amber-500/15 text-amber-800 dark:text-amber-200';
    return 'bg-slate-500/15 text-slate-700 dark:text-slate-300';
  };

  const applyFilters = () => setAppliedFilters({ ...filterForm });
  const resetFilters = () => { const d = defaultFilters(); setFilterForm(d); setAppliedFilters(d); };
  const appliedFiltersSummary = useMemo(() => buildAppliedFiltersSummary(appliedFilters, t, users), [appliedFilters, t, users]);

  const newTicketSummary = useMemo(() => {
    const title = form.title.trim();
    return title || t('admin.planningBoard.collapsedNewTicketHint');
  }, [form.title, t]);

  const lastSyncLabel = useMemo(() => {
    if (!syncMeta.lastChecked) return null;
    try {
      return new Date(syncMeta.lastChecked).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
    } catch { return syncMeta.lastChecked; }
  }, [syncMeta.lastChecked]);

  const syncHasPendingOrOutdated = syncMeta.pendingCount > 0 || syncMeta.outdatedCount > 0;
  const syncNeedsAttention =
    !!syncMeta.error
    || (syncMeta.loaded && syncMeta.available === false)
    || syncHasPendingOrOutdated;

  return (
    <div className="flex flex-col gap-6 p-fluid-sm pb-10">
      {/* Page title */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('admin.planningBoard.title')}</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-white/70">{t('admin.planningBoard.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isTestEnv ? (
            <button
              type="button"
              onClick={() => setSyncInfoDialogOpen(true)}
              className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
              title={
                syncNeedsAttention
                  ? `${t('admin.planningBoard.syncInfoButton')} — ${t('admin.planningBoard.syncInfoAttentionHint')}`
                  : t('admin.planningBoard.syncInfoButton')
              }
              aria-label={
                syncNeedsAttention
                  ? `${t('admin.planningBoard.syncInfoButton')}. ${t('admin.planningBoard.syncInfoAttentionHint')}`
                  : t('admin.planningBoard.syncInfoButton')
              }
            >
              <Info className="h-4 w-4 text-indigo-600 dark:text-indigo-300" aria-hidden />
              {syncNeedsAttention ? (
                <span
                  className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white dark:ring-indigo-500"
                  aria-hidden
                />
              ) : null}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => loadTickets()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {t('admin.planningBoard.refresh')}
          </button>
        </div>
      </div>

      {/* Dialog stato sync TEST → PROD (solo TEST, aperto su richiesta) */}
      {isTestEnv ? (
        <Dialog open={syncInfoDialogOpen} onOpenChange={setSyncInfoDialogOpen}>
          <DialogContent className="max-w-lg gap-0 overflow-hidden p-0 dark:border-white/10 dark:bg-gray-900 sm:rounded-xl">
            <DialogHeader className="border-b border-gray-100 px-6 py-4 text-left dark:border-white/10">
              <DialogTitle>{t('admin.planningBoard.syncBannerTitle')}</DialogTitle>
              <DialogDescription>{t('admin.planningBoard.syncInfoDialogDescription')}</DialogDescription>
            </DialogHeader>
            <div className="px-6 pb-6 pt-4">
              <div className={`rounded-xl border p-3 shadow-sm dark:border-white/10 ${
                syncMeta.error || (syncMeta.loaded && syncMeta.available === false)
                  ? 'border-amber-300 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30'
                  : !syncMeta.loaded || (syncMeta.checking && !syncMeta.lastChecked)
                    ? 'border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-gray-900/60'
                    : syncHasPendingOrOutdated
                      ? 'border-sky-300 bg-sky-50 dark:border-sky-900/40 dark:bg-sky-950/25'
                      : 'border-emerald-200 bg-emerald-50/80 dark:border-emerald-900/40 dark:bg-emerald-950/20'
              }`}>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start sm:gap-x-4">
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {syncMeta.error || (syncMeta.loaded && syncMeta.available === false)
                        ? <AlertTriangle className="h-5 w-5 shrink-0 text-amber-800 dark:text-amber-400" aria-hidden />
                        : !syncMeta.loaded || (syncMeta.checking && !syncMeta.lastChecked)
                          ? <RefreshCw className="h-5 w-5 shrink-0 animate-spin text-gray-600 dark:text-white/70" aria-hidden />
                          : syncHasPendingOrOutdated
                            ? <Info className="h-5 w-5 shrink-0 text-sky-700 dark:text-sky-300" aria-hidden />
                            : <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-400" aria-hidden />
                      }
                    </div>
                    {syncMeta.checking && !syncMeta.loaded && (
                      <p className="text-sm text-gray-700 dark:text-white/80">{t('admin.planningBoard.syncBannerChecking')}</p>
                    )}
                    {syncMeta.checking && syncMeta.loaded && (
                      <p className="text-xs text-gray-600 dark:text-white/65">{t('admin.planningBoard.syncBannerRechecking')}</p>
                    )}
                    {syncMeta.error && (
                      <p className="text-sm text-amber-900 dark:text-amber-100/90">{syncMeta.error}</p>
                    )}
                    {!syncMeta.error && syncMeta.loaded && syncMeta.available === false && syncMeta.serverMessage && (
                      <p className="text-sm text-amber-950 dark:text-amber-50/90">{syncMeta.serverMessage}</p>
                    )}
                    {!syncMeta.error && syncMeta.loaded && syncMeta.available === false && !syncMeta.serverMessage && (
                      <p className="text-sm text-amber-950 dark:text-amber-50/90">{t('admin.planningBoard.syncBannerUnavailable')}</p>
                    )}
                    {!syncMeta.error && syncMeta.loaded && syncMeta.available && syncMeta.pendingCount > 0 && (
                      <p className="text-sm text-sky-950 dark:text-sky-50/90">
                        {t('admin.planningBoard.syncBannerOkPending', { count: syncMeta.pendingCount })}
                      </p>
                    )}
                    {!syncMeta.error && syncMeta.loaded && syncMeta.available && syncMeta.outdatedCount > 0 && (
                      <p className="text-sm text-sky-950 dark:text-sky-50/90">
                        {t('admin.planningBoard.syncBannerOkOutdated', { count: syncMeta.outdatedCount })}
                      </p>
                    )}
                    {!syncMeta.error && syncMeta.loaded && syncMeta.available && !syncHasPendingOrOutdated && (
                      <p className="text-sm text-emerald-950 dark:text-emerald-50/90">{t('admin.planningBoard.syncBannerOkAligned')}</p>
                    )}
                    {lastSyncLabel && (
                      <p className="text-xs text-gray-600 dark:text-white/55">
                        {t('admin.planningBoard.syncBannerLastCheck', { time: lastSyncLabel })}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => refreshProdSync()}
                    disabled={syncMeta.checking}
                    className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-lg border border-gray-900/10 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 disabled:opacity-60 dark:border-white/15 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800 sm:w-auto sm:justify-self-end"
                  >
                    <RefreshCw className={`h-4 w-4 ${syncMeta.checking ? 'animate-spin' : ''}`} />
                    {t('admin.planningBoard.syncCheckButton')}
                  </button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-white/10 dark:bg-gray-900/90">
          <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-white/60">{t('admin.planningBoard.kpis.activeBoard')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">{boardStats.totalActive}</p>
        </div>
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/70 px-4 py-3 shadow-sm dark:border-indigo-900/40 dark:bg-indigo-950/30">
          <p className="text-[11px] uppercase tracking-wide text-indigo-700/80 dark:text-indigo-300">{t('admin.planningBoard.kpis.inSprint')}</p>
          <p className="mt-1 text-xl font-semibold text-indigo-900 dark:text-indigo-100">{boardStats.inSprint}</p>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 shadow-sm dark:border-emerald-900/40 dark:bg-emerald-950/30">
          <p className="text-[11px] uppercase tracking-wide text-emerald-700/80 dark:text-emerald-300">{t('admin.planningBoard.kpis.testingDone')}</p>
          <p className="mt-1 text-xl font-semibold text-emerald-900 dark:text-emerald-100">{boardStats.testingDone}</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 shadow-sm dark:border-amber-900/40 dark:bg-amber-950/30">
          <p className="text-[11px] uppercase tracking-wide text-amber-700/80 dark:text-amber-300">{t('admin.planningBoard.kpis.testingBlocked')}</p>
          <p className="mt-1 text-xl font-semibold text-amber-900 dark:text-amber-100">{boardStats.testingBlocked}</p>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
        {/* Filters */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-gray-900/90">
          <button
            type="button"
            onClick={() => setFiltersExpanded((v) => !v)}
            aria-expanded={filtersExpanded}
            className="flex w-full items-start justify-between gap-3 rounded-xl p-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/5"
          >
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-white/60">
                {t('admin.planningBoard.filtersTitle')}
              </h2>
              {!filtersExpanded && (
                <p className="mt-1 truncate text-xs text-gray-600 dark:text-white/65">{appliedFiltersSummary}</p>
              )}
            </div>
            <ChevronDown
              className={`mt-0.5 h-5 w-5 shrink-0 text-gray-500 transition-transform dark:text-white/60 ${filtersExpanded ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </button>
          {filtersExpanded && (
            <div className="border-t border-gray-100 px-4 pb-4 pt-2 dark:border-white/10">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.filterArea')}</span>
                  <select value={filterForm.area} onChange={(e) => setFilterForm((f) => ({ ...f, area: e.target.value }))}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 dark:border-white/10 dark:bg-gray-950 dark:text-white">
                    <option value="all">{t('admin.planningBoard.filterAll')}</option>
                    {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.filterStatus')}</span>
                  <select value={filterForm.status} onChange={(e) => setFilterForm((f) => ({ ...f, status: e.target.value }))}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 dark:border-white/10 dark:bg-gray-950 dark:text-white">
                    <option value="all">{t('admin.planningBoard.filterAll')}</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{t(`admin.planningBoard.statuses.${s}`)}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.filterAssignee')}</span>
                  <select
                    value={filterForm.assignee_user_id}
                    onChange={(e) => setFilterForm((f) => ({ ...f, assignee_user_id: e.target.value }))}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 dark:border-white/10 dark:bg-gray-950 dark:text-white"
                  >
                    <option value="">{t('admin.planningBoard.filterAll')}</option>
                    <option value="unassigned">{t('admin.planningBoard.filterUnassigned')}</option>
                    {adminAssigneeOptions.map((u) => (
                      <option key={u.id} value={String(u.id)}>{userLabel(u)}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" onClick={applyFilters} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-[#5529a8]">
                  {t('admin.planningBoard.applyFilters')}
                </button>
                <button type="button" onClick={resetFilters} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:bg-gray-950 dark:text-white dark:hover:bg-white/10">
                  {t('admin.planningBoard.resetFilters')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* New ticket form */}
        <form onSubmit={handleCreate} className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-gray-900/90">
          <button type="button" onClick={() => setNewTicketExpanded((v) => !v)} aria-expanded={newTicketExpanded}
            className="flex w-full items-start justify-between gap-3 rounded-xl p-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/5">
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-white/60">
                {t('admin.planningBoard.newTicket')}
              </h2>
              {!newTicketExpanded && (
                <p className="mt-1 truncate text-xs text-gray-600 dark:text-white/65">{newTicketSummary}</p>
              )}
            </div>
            <ChevronDown className={`mt-0.5 h-5 w-5 shrink-0 text-gray-500 transition-transform dark:text-white/60 ${newTicketExpanded ? 'rotate-180' : ''}`} aria-hidden />
          </button>
          {newTicketExpanded && (
            <div className="border-t border-gray-100 px-4 pb-4 pt-2 dark:border-white/10">
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-2 2xl:grid-cols-4">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.area')}</span>
                  <select value={form.area} onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 dark:border-white/10 dark:bg-gray-950 dark:text-white">
                    {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm md:col-span-2 xl:col-span-1 2xl:col-span-2">
                  <span className="text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.title')}</span>
                  <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    maxLength={255} placeholder={t('admin.planningBoard.placeholders.title')}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 dark:border-white/10 dark:bg-gray-950 dark:text-white" />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.targetDate')}</span>
                  <input type="date" value={form.target_date} onChange={(e) => setForm((f) => ({ ...f, target_date: e.target.value }))}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 dark:border-white/10 dark:bg-gray-950 dark:text-white" />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.priority')}</span>
                  <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 dark:border-white/10 dark:bg-gray-950 dark:text-white">
                    {PRIORITIES.map((p) => <option key={p} value={p}>{t(`admin.planningBoard.priorities.${p}`)}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.initialStatus')}</span>
                  <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 dark:border-white/10 dark:bg-gray-950 dark:text-white">
                    {STATUSES.map((s) => <option key={s} value={s}>{t(`admin.planningBoard.statuses.${s}`)}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.testingStatus')}</span>
                  <select value={form.testing_status} onChange={(e) => setForm((f) => ({ ...f, testing_status: e.target.value }))}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 dark:border-white/10 dark:bg-gray-950 dark:text-white">
                    {TESTING_STATUSES.map((ts) => <option key={ts} value={ts}>{t(`admin.planningBoard.testingStatuses.${ts}`)}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm md:col-span-2 xl:col-span-2 2xl:col-span-2">
                  <span className="text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.assignee')}</span>
                  <select value={form.assignee_user_id} onChange={(e) => setForm((f) => ({ ...f, assignee_user_id: e.target.value }))}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 dark:border-white/10 dark:bg-gray-950 dark:text-white">
                    <option value="">{t('admin.planningBoard.assigneeNone')}</option>
                    {newTicketAssigneeOptions.map((u) => <option key={u.id} value={String(u.id)}>{userLabel(u)}</option>)}
                  </select>
                </label>
                <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm md:col-span-2 xl:col-span-2 2xl:col-span-2 dark:border-white/10 dark:bg-gray-950/90">
                  <input
                    type="checkbox"
                    checked={form.in_sprint}
                    onChange={(e) => setForm((f) => ({ ...f, in_sprint: e.target.checked, sprint_name: e.target.checked ? f.sprint_name : '' }))}
                    className="h-4 w-4 shrink-0 rounded border-gray-300 bg-white accent-indigo-600 dark:border-white/40 dark:bg-gray-900 dark:accent-indigo-400"
                  />
                  <span className="text-gray-800 dark:text-white">{t('admin.planningBoard.fields.inSprint')}</span>
                </label>
                {form.in_sprint ? (
                  <label className="flex flex-col gap-1 text-sm md:col-span-2 xl:col-span-2 2xl:col-span-2">
                    <span className="text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.sprintName')}</span>
                    <input
                      value={form.sprint_name}
                      onChange={(e) => setForm((f) => ({ ...f, sprint_name: e.target.value }))}
                      placeholder={t('admin.planningBoard.placeholders.sprintName')}
                      maxLength={120}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 dark:border-white/10 dark:bg-gray-950 dark:text-white"
                    />
                  </label>
                ) : null}
                <label className="flex flex-col gap-1 text-sm md:col-span-2 lg:col-span-4 xl:col-span-2 2xl:col-span-4">
                  <span className="text-gray-600 dark:text-white/70">{t('admin.planningBoard.fields.description')}</span>
                  <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    rows={2} placeholder={t('admin.planningBoard.placeholders.description')}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 dark:border-white/10 dark:bg-gray-950 dark:text-white" />
                </label>
              </div>
              <div className="mt-4 flex justify-end">
                <button type="submit" disabled={saving}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-[#5529a8] disabled:opacity-50">
                  <Plus className="h-4 w-4" />
                  {t('admin.planningBoard.create')}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Vista ticket: tabella compatta */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
        </div>
      ) : (
        <div className="flex min-h-0 flex-col gap-3">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setArchiveDialogOpen(true)}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
            >
              <Archive className="h-4 w-4" />
              {t('admin.planningBoard.archiveButton', { count: archivedTickets.length })}
            </button>
          </div>

          <PlanningTicketsDenseTable
            tickets={mainListTickets}
            t={t}
            getAssigneeSelectOptions={getAssigneeSelectOptions}
            onStatusChange={handleStatusChange}
            onAssigneeChange={handleAssigneeChange}
            onPriorityChange={handlePriorityChange}
            onTestingStatusChange={handleTestingStatusChange}
            onDelete={handleDelete}
            onOpenDetail={(tk) => setDetailModalTicket(tk)}
            isTestEnv={isTestEnv}
            getProdStatus={getProdStatus}
          />
        </div>
      )}

      {/* Archive dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent className="flex max-h-[min(92vh,860px)] w-[min(96vw,1180px)] max-w-[min(96vw,1180px)] flex-col gap-0 overflow-hidden p-0 dark:border-white/10 dark:bg-gray-900 sm:rounded-xl">
          <DialogHeader className="shrink-0 border-b border-gray-100 px-6 py-4 dark:border-white/10">
            <DialogTitle className="text-lg text-gray-900 dark:text-white">{t('admin.planningBoard.archiveTitle')}</DialogTitle>
            <p className="text-sm font-normal text-gray-600 dark:text-white/70">{t('admin.planningBoard.archiveSubtitle')}</p>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-auto px-3 py-3 sm:px-4">
            {archivedTickets.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500 dark:text-white/60">{t('admin.planningBoard.archiveEmpty')}</p>
            ) : (
              <PlanningTicketsDenseTable
                tickets={archivedTickets}
                t={t}
                getAssigneeSelectOptions={getAssigneeSelectOptions}
                onStatusChange={handleStatusChange}
                onAssigneeChange={handleAssigneeChange}
                onPriorityChange={handlePriorityChange}
                onTestingStatusChange={handleTestingStatusChange}
                onDelete={handleDelete}
                onOpenDetail={(tk) => { setArchiveDialogOpen(false); setDetailModalTicket(tk); }}
                isTestEnv={isTestEnv}
                getProdStatus={getProdStatus}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail modal */}
      <TicketDetailModal
        open={detailModalTicket !== null}
        onClose={() => setDetailModalTicket(null)}
        ticket={detailModalTicket}
        api={api}
        t={t}
        isTestEnv={isTestEnv}
        getAssigneeSelectOptions={getAssigneeSelectOptions}
        adminAssigneeOptions={adminAssigneeOptions}
        priorityClass={priorityClass}
        pendingProdCodes={pendingProdCodes}
        outdatedProdCodes={outdatedProdCodes}
        onSaved={loadTickets}
        onPushToProd={handlePushToProd}
        pushBusy={pushingTicketId === detailModalTicket?.id}
        onUpdateInProd={handleUpdateInProd}
        updateBusy={updatingTicketId === detailModalTicket?.id}
      />
    </div>
  );
};

export default AdminPlanningBoard;
